import { cliSimpleGit } from '../../cli-simple-git.js';
import { createUntrackedStatsApplier } from './apply-untracked-file-stats.js';
import { buildChangedFileRowsFromPaths } from './build-changed-file-rows.js';
import { enrichWorkingTreeFileRows } from './enrich-working-tree-file-rows.js';
import { collectChangedPaths, parseDiffOutput } from './parse-diff-output.js';
import { RENAME_DIFF_ARGS } from './rename-diff-args.js';
import { dedupeChangedFileRows } from '../rows/dedupe-changed-file-rows.js';
import type { WorkingTreeChangeFileRow } from '../types.js';
import type { ListChangedFilesOptions } from './list-changed-files-options.js';

export async function listChangedFilesForRepo(
  repoGitCwd: string,
  repoPathRelativeToWorkspaceRoot: string,
  options: ListChangedFilesOptions = {},
): Promise<WorkingTreeChangeFileRow[]> {
  const g = cliSimpleGit(repoGitCwd);
  const [nameStatusRaw, numstatRaw, untrackedRaw] = await Promise.all([
    g.raw(['diff', ...RENAME_DIFF_ARGS, '--name-status', 'HEAD']).catch(() => ''),
    g.raw(['diff', ...RENAME_DIFF_ARGS, 'HEAD', '--numstat']).catch(() => ''),
    g.raw(['ls-files', '--others', '--exclude-standard']).catch(() => ''),
  ]);

  const parsed = parseDiffOutput(String(nameStatusRaw), String(numstatRaw));
  const untracked = String(untrackedRaw)
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);
  const untrackedSet = new Set(untracked);
  const paths = collectChangedPaths({
    nameEntries: parsed.nameEntries,
    numByPath: parsed.numByPath,
    untracked,
  });

  let rows = await buildChangedFileRowsFromPaths({
    paths,
    repoPathRelativeToWorkspaceRoot,
    nameByPath: parsed.nameByPath,
    numByPath: parsed.numByPath,
    movedSourcePaths: parsed.movedSourcePaths,
    untrackedSet,
    applyUntrackedStats: createUntrackedStatsApplier({
      g,
      repoGitCwd,
      untrackedSet,
      nameByPath: parsed.nameByPath,
    }),
  });

  rows = dedupeChangedFileRows(rows);
  if (options.includeFilePatches) {
    await enrichWorkingTreeFileRows({
      rows,
      repoGitCwd,
      repoPathRelativeToWorkspaceRoot,
      binaryByPath: parsed.binaryByPath,
    });
  }
  return dedupeChangedFileRows(rows);
}
